import { useState, useEffect } from 'react'
import { Modal } from './modal'
import { StatusBadge } from './StatusBadge'
import { DispatchTimeline } from './DispatchTimeline'
import { AuditEventsList } from './AuditEventsList'
import { useBonusInstance, useTriggerInstanceWebhook } from '@/hooks/use-bonuses'
import { formatPeriodLabel, type BonusDispatchStatus, type Timeframe } from '@/types'

interface InstanceDetailModalProps {
  instanceId: string | null
  isOpen: boolean
  onClose: () => void
}

type TabId = 'overview' | 'dispatch' | 'audit'

export function InstanceDetailModal({ instanceId, isOpen, onClose }: InstanceDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const { data: instance, isLoading } = useBonusInstance(instanceId || '', !!instanceId)

  // Reset tab when viewing different instance
  useEffect(() => {
    if (instanceId) setActiveTab('overview')
  }, [instanceId])
  const triggerWebhook = useTriggerInstanceWebhook()

  if (!instanceId) return null

  const handleDispatch = async () => {
    if (confirm('Dispatch this bonus to HR?')) {
      await triggerWebhook.mutateAsync(instanceId)
    }
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'dispatch', label: 'Dispatch History' },
    { id: 'audit', label: 'Audit Trail' },
  ]

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Period Details" size="lg">
      {isLoading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-100 rounded w-1/2" />
          <div className="h-4 bg-gray-100 rounded w-3/4" />
          <div className="h-32 bg-gray-100 rounded" />
        </div>
      ) : instance ? (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {formatPeriodLabel(instance.period_start, instance.goal_timeframe as Timeframe)}
              </h3>
              <p className="text-sm text-gray-500">
                {instance.goal_metric_name} • {instance.user_name}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {new Date(instance.period_start * 1000).toLocaleDateString()} -
                {new Date(instance.period_end * 1000).toLocaleDateString()}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                {instance.config_currency === 'USD' ? '$' : instance.config_currency}
                {instance.config_amount?.toLocaleString()}
              </div>
              <StatusBadge status={instance.dispatch_status as BonusDispatchStatus} size="md" />
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-brand-teal text-brand-teal'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="min-h-[300px]">
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <InfoRow label="Period" value={formatPeriodLabel(instance.period_start, instance.goal_timeframe as Timeframe)} />
                  <InfoRow label="Met Goal" value={instance.is_met ? 'Yes' : 'No'} />
                  <InfoRow label="Metric Value" value={instance.metric_value_at_close?.toString() || 'N/A'} />
                  <InfoRow label="Dispatch Status" value={instance.dispatch_status} />
                  <InfoRow label="Attempts" value={String(instance.dispatch_attempts || 0)} />
                </div>

                {/* Dispatch button for met, pending instances */}
                {instance.is_met && instance.dispatch_status === 'pending' && (
                  <div className="pt-4 border-t">
                    <button
                      onClick={handleDispatch}
                      disabled={triggerWebhook.isPending}
                      className="btn btn-primary"
                    >
                      {triggerWebhook.isPending ? 'Dispatching...' : 'Dispatch to HR'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'dispatch' && (
              <DispatchTimeline key={`dispatch-${instanceId}`} instanceId={instanceId} />
            )}

            {activeTab === 'audit' && (
              <AuditEventsList key={`audit-${instanceId}`} instanceId={instanceId} />
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">Instance not found</div>
      )}
    </Modal>
  )
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900">{value || '—'}</dd>
    </div>
  )
}
